import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Flatpickr from 'react-flatpickr';
import moment from 'moment';
import { useIntl } from 'react-intl';
import {
  Label,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';

import 'flatpickr/dist/themes/airbnb.css';

import UpgradePlanButton from '../UpgradePlanButton';

import {
  getRawDate,
  getUserTimeZone,
} from '../../helpers/DateTime';
import { userHasFeature } from '../../helpers/Features';
import { getSetting } from '../../helpers/Settings';

const UploadFileExpirationDate = ({
  selectedFile,
  selectedFiles,
  isEdit,
  handleFormChange,
}) => {
  const intl = useIntl();

  const getDateValue = () => {
    if (isEdit && selectedFiles.length === 1) {
      return selectedFiles[0].expires_at;
    }

    if (selectedFiles.length > 1) {
      return null;
    }

    if (selectedFile) {
      return selectedFile.expires_at;
    }

    return null;
  };

  const expirationDateRequired = getSetting('expiration_date') && getSetting('expiration_date_required');
  const expirationDateValidationError = selectedFile ?
    selectedFile.errors?.expirationDate :
    !!selectedFiles.find(f => f.errors?.expirationDate);

  return (
    <>
      <Label for="selectFileExpirationDate">
        {intl.formatMessage({ defaultMessage: 'Expiration date' })}
        {expirationDateRequired && (
          <span
            className="required-asterisk"
            id="expiration-date-required-asterisk"
          >
            *
            <UncontrolledTooltip fade={false} placement="bottom" target="expiration-date-required-asterisk">
              {intl.formatMessage({ defaultMessage: 'Required' })}
            </UncontrolledTooltip>
          </span>
        )}
        <UpgradePlanButton
          feature="expiration-date"
          simple={true}
        />
      </Label>
      {expirationDateValidationError && (
        <div className="invalid-feedback" style={{ display: 'block' }}>
          {intl.formatMessage({ defaultMessage: 'This field is required' })}
        </div>
      )}
      <div className={classnames('date-picker-container', { disabled: !userHasFeature('expiration-date') })}>
        <Flatpickr
          render={
            ({ defaultValue, value, ...props }, ref) => (
              <Input {...props} defaultValue={defaultValue} innerRef={ref} />
            )
          }
          value={getDateValue()}
          disabled={!userHasFeature('expiration-date')}
          onChange={(dates, date) => {
            const now = getRawDate(moment(`${date} ${moment().format('HH:mm:ss')}`).tz(getUserTimeZone()));
            const dateTime = now.utc().format('YYYY-MM-DD HH:mm:ss');
            handleFormChange({ target: { name: 'fileExpirationDate', value: dateTime } });
          }}
        />
        <a
          href="#clear"
          className="input-clear"
          title={intl.formatMessage({ defaultMessage: 'Clear' })}
          onClick={(e) => {
            e.preventDefault();
            handleFormChange({ target: { name: 'fileExpirationDate', value: null } });
          }}
        >
          <i className="fas fa-times" />
        </a>
      </div>
    </>
  );
};

UploadFileExpirationDate.propTypes = {
  isEdit: PropTypes.bool.isRequired,
  selectedFile: PropTypes.object,
  selectedFiles: PropTypes.array.isRequired,
  handleFormChange: PropTypes.func.isRequired,
};

UploadFileExpirationDate.defaultProps = {
  selectedFile: null,
};

export default UploadFileExpirationDate;
